import React from 'react';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import downloadSmallTokens from '@amzn/meridian-tokens/base/icon/download-small';
import checkCircleTokens from '@amzn/meridian-tokens/base/icon/check-circle';
import clockTokens from '@amzn/meridian-tokens/base/icon/clock';
import userTokens from '@amzn/meridian-tokens/base/icon/user';
import { MetricName } from '../dashboard/constants';
import { RECEIVED_ROW_KEY, formatFlowNumber, formatVariance, metricRowLabel } from './utils';

/**
 * Icon per contract field, keyed the same way as METRIC_ROW_LABELS.
 *
 * A name with no entry here renders with no icon rather than borrowing another metric's —
 * an unrecognised metric must never be mislabelled as a known one. There is deliberately
 * no buffer icon: the contract has no buffer metric.
 */
const ROW_ICON_TOKENS: Record<string, object> = {
    [RECEIVED_ROW_KEY]: downloadSmallTokens,
    [MetricName.VOLUME_PROCESSED]: checkCircleTokens,
    [MetricName.UPH]: clockTokens,
    [MetricName.HEADCOUNT]: userTokens,
};

/**
 * The leading icon for a metric row, on its own. Decorative: Meridian renders an Icon
 * with no children as `aria-hidden`, and callers always pair it with a text label.
 *
 * Exported so the legend can show the same icon set the cards use, from one mapping.
 * Renders nothing for a name with no icon.
 */
export const MetricRowIcon: React.FC<{ rowKey: string }> = ({ rowKey }) => {
    const tokens = ROW_ICON_TOKENS[rowKey];
    return tokens == null ? null : <Icon tokens={tokens} />;
};

export interface MetricRowProps {
    /**
     * A `MetricName` value, RECEIVED_ROW_KEY, or any name the service sent that we do not
     * recognise — the label falls back to the raw name.
     */
    rowKey: string;
    actualValue: number | null | undefined;
    /**
     * The target to compare against. Omit entirely for a field the contract gives no
     * target for (`received`), and the row renders a single number instead of `a / b`.
     */
    expectedValue?: number | null;
    /** Omit for a field the contract gives no variance for (`received`). */
    variancePercentage?: number | null;
    'data-testid'?: string;
}

/**
 * One compact metric row on a flow card: a small leading icon, a short text label, then
 * `actual / expected` and the signed variance.
 *
 * Shared so the Andon view's per-path cards can adopt the same format later; only the
 * Volume Flow map node cards use it today.
 *
 * The icon never carries meaning on its own — it is decorative (Meridian renders an Icon
 * with no children as `aria-hidden`) and the text label sits right next to it, so the row
 * reads correctly on a screen reader and in greyscale.
 *
 * `expectedValue` / `variancePercentage` are optional rather than nullable-and-always-
 * passed on purpose: a field the contract has no target for (`received` is a bare number)
 * renders as one number, with no invented target and no em-dashed placeholder.
 */
const MetricRow: React.FC<MetricRowProps> = ({
    rowKey,
    actualValue,
    expectedValue,
    variancePercentage,
    'data-testid': testId,
}) => {
    const hasExpected = expectedValue !== undefined;
    const hasVariance = variancePercentage !== undefined;

    return (
        <Row spacing="200" alignmentVertical="center" widths="fit" wrap="down" data-testid={testId}>
            <MetricRowIcon rowKey={rowKey} />
            <Text type="b100" color="secondary">{metricRowLabel(rowKey)}</Text>
            <Text type="b200">
                {hasExpected
                    ? `${formatFlowNumber(actualValue)} / ${formatFlowNumber(expectedValue)}`
                    : formatFlowNumber(actualValue)}
            </Text>
            {hasVariance && (
                <Text type="b100" color="secondary">{formatVariance(variancePercentage)}</Text>
            )}
        </Row>
    );
};

export default MetricRow;
